import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import loginPageImg from "../images/loginpage.png";
import inventrakLogo from "../images/inventory_logo.png";
import { useForm } from "react-hook-form";
import { callAPI, customValidation, LoacalVariables, Method, setValue, validations } from "../commonFunctions/CommonFunctions";
import { useSnackbar } from "../Components/Snackbar/SnackBarContext";
import { useNavigate } from "react-router-dom";

interface LoginFormProps {
  onLogin: (accessToken: string, refreshToken?: string) => void;
}

const Login: React.FC<LoginFormProps> = ({ onLogin }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const request = {
        email: data.email,
        password: data.password,
      };

      const response = await callAPI(
        "auth/login",
        Method.POST,
        request
      );

      if (response) {
        onLogin(response?.accessToken, response?.refreshToken);
        setValue(LoacalVariables.UserId, response?.user.id)
        setValue(LoacalVariables.UserType, response?.user.role)
        setValue(LoacalVariables.Name, response?.user.full_name);
        setValue(LoacalVariables.Email, response?.user.email)

        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/");
        }
      }
    } catch (error: any) {
      console.error("Failed to login:", error);
      showMessage(error.response?.data?.message || "Failed to login user", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
      .login-input::placeholder {
        color: rgba(180,180,180,0.55) !important;
        opacity: 1 !important;
      }
      .login-input::-webkit-input-placeholder {
        color: rgba(180,180,180,0.55) !important;
        opacity: 1 !important;
        -webkit-text-fill-color: rgba(180,180,180,0.55) !important;
      }
      .login-input::-moz-placeholder {
        color: rgba(180,180,180,0.55) !important;
        opacity: 1 !important;
      }
    `}</style>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${loginPageImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.3)",
          },
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: 600,
            borderRadius: 4,
            position: "relative",
            overflow: "hidden",
            backdropFilter: "blur(4px)",
            background: "rgba(80, 80, 80, 0.35)",
            border: "1.5px solid rgba(255,255,255,0.7)",
            boxShadow: `0 8px 32px rgba(0,0,0,0.3)`,
          }}
        >
          <CardContent sx={{ p: 4, pt: 3, pb: 2 }}>
            {}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              <Box
                component="img"
                src={inventrakLogo}
                alt="Inventrak Logo"
                sx={{ width: 160, objectFit: "contain" }}
              />
            </Box>

            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 0.5,
                fontWeight: 700,
                color: "#ffffff",
                letterSpacing: "-0.5px",
              }}
            >
              Welcome Back!
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={0}>
                {}
                <Grid item xs={12}>
                  <Typography sx={{ color: "#d4ff00", fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
                    Email
                  </Typography>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: `1px solid ${errors.email ? "rgba(255,80,80,0.8)" : "rgba(255,255,255,0.5)"}`,
                    pb: 0.5,
                  }}>
                    <input
                      {...register("email", customValidation(false, validations.email))}
                      type="text"
                      placeholder="Enter your email"
                      className="login-input"
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#fff",
                        fontSize: "16px",
                        padding: "8px 0",
                        fontFamily: "inherit",
                        WebkitBoxShadow: "0 0 0 1000px transparent inset",
                        WebkitTextFillColor: "#fff",
                        caretColor: "#fff",
                        transition: "background-color 9999s ease-in-out 0s",
                      }}
                    />
                    {watch("email") && !errors.email && (
                      <CheckIcon sx={{ color: "#d4ff00", fontSize: 20, flexShrink: 0 }} />
                    )}
                  </Box>
                  {errors.email && (
                    <Typography sx={{ color: "rgba(255,80,80,0.9)", fontSize: "12px", mt: 0.5 }}>
                      {errors.email.message as string}
                    </Typography>
                  )}
                </Grid>

                {}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography sx={{ color: "#d4ff00", fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
                    Password
                  </Typography>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: `1px solid ${errors.password ? "rgba(255,80,80,0.8)" : "rgba(255,255,255,0.5)"}`,
                    pb: 0.5,
                  }}>
                    <input
                      {...register("password", {
                        required: "Password is required",
                        minLength: { value: 8, message: "Password must be at least 8 characters" },
                      })}
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="login-input"
                      style={{
                        flex: 1,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        color: "#fff",
                        fontSize: "16px",
                        padding: "8px 0",
                        fontFamily: "inherit",
                        WebkitBoxShadow: "0 0 0 1000px transparent inset",
                        WebkitTextFillColor: "#fff",
                        caretColor: "#fff",
                        transition: "background-color 9999s ease-in-out 0s",
                      }}
                    />
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} sx={{ p: 0, flexShrink: 0 }}>
                      {showPassword
                        ? <VisibilityOff sx={{ color: "#d4ff00", fontSize: 20 }} />
                        : <Visibility sx={{ color: "#d4ff00", fontSize: 20 }} />
                      }
                    </IconButton>
                  </Box>
                  {errors.password && (
                    <Typography sx={{ color: "rgba(255,80,80,0.9)", fontSize: "12px", mt: 0.5 }}>
                      {errors.password.message as string}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {}

              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={!isValid || loading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 2,
                  fontSize: "1rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  background: "#d4ff00",
                  color: "#000",
                  boxShadow: "0 0 15px rgba(212,255,0,0.4)",
                  "&:hover": {
                    background: "#c0e600",
                    boxShadow: "0 0 20px rgba(212,255,0,0.6)",
                  },
                  "&:disabled": {
                    background: "rgba(212,255,0,0.4)",
                    boxShadow: "none",
                  },
                }}
              >
                Login
              </Button>

              {}
              {}

              {}
              <Divider sx={{ borderColor: "rgba(255,255,255,0.3)", mt: 2, mb: 1.5 }} />
              <Typography
                align="center"
                sx={{ color: "rgba(255,255,255,0.6)", fontSize: "13px" }}
              >
                Powered by Willsoar Technologies
              </Typography>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default Login;